import React from "react";
import { colorFromString, initials, timeAgo } from "../lib/format";
import { TrendingUp, TrendingDown, X } from "lucide-react";

/**
 * ClearFiltersButton — a small "Clear filters" pill that appears only when at least one
 * filter value differs from its default. On click, resets all filters to defaults.
 *
 * Usage:
 *   <ClearFiltersButton filters={filters} defaults={{ q:"", status:"" }} onClear={setFilters} />
 */
export function ClearFiltersButton({ filters, defaults, onClear, testid = "clear-filters" }) {
  const hasActive = Object.keys(defaults).some((k) => (filters?.[k] ?? "") !== (defaults[k] ?? ""));
  if (!hasActive) return null;
  return (
    <button
      type="button"
      onClick={() => onClear({ ...defaults })}
      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border border-[var(--brand)] text-[var(--brand)] hover:bg-[var(--brand)] hover:text-white transition-colors whitespace-nowrap"
      data-testid={testid}
      title="Reset all filters"
    >
      <X size={12} strokeWidth={2.4} /> Clear filters
    </button>
  );
}

export function DeltaBadge({ value }) {
  if (value === null || value === undefined || isNaN(Number(value))) return null;
  const v = Number(value);
  const positive = v >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${positive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="delta-badge">
      <Icon size={11} strokeWidth={2.2} />
      {positive ? "+" : ""}{Math.round(v * 10) / 10}%
    </span>
  );
}

// --- Status normalization ----------------------------------------------------
// Map raw sheet values like ACKNOWLEDGED_YES → pretty label + colour.
// Admins can override per-campaign via Configurator → Status labels.
const TITLECASE = (s) => String(s).toLowerCase().replace(/[_\-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();

export const DEFAULT_STATUS_MAP = {
  // Opt-in lifecycle
  ACKNOWLEDGED_YES: { label: "Opted In", color: "green" },
  ACKNOWLEDGED_NO:  { label: "Opted Out", color: "red" },
  OPT_OUT_BY_CREATOR: { label: "Opt Out", color: "red" },
  OPTED_OUT: { label: "Opted Out", color: "red" },
  OPTED_IN: { label: "Opted In", color: "green" },
  INITIALIZED: { label: "Pending", color: "amber" },
  IN_REVIEW: { label: "In Review", color: "amber" },
  // Content lifecycle
  CONTENT_PENDING:  { label: "Pending", color: "amber" },
  CONTENT_APPROVED: { label: "Approved", color: "green" },
  CONTENT_REJECTED: { label: "Rejected", color: "red" },
  CONTENT_LIVE:     { label: "Live", color: "green" },
  PENDING:  { label: "Pending", color: "amber" },
  APPROVED: { label: "Approved", color: "green" },
  REJECTED: { label: "Rejected", color: "red" },
  LIVE:     { label: "Live", color: "green" },
  ACTIVE:   { label: "Active", color: "green" },
  COMPLETED:{ label: "Completed", color: "gray" },
  EXPIRED:  { label: "Expired", color: "red" },
  PAID:     { label: "Paid", color: "green" },
  OVERDUE:  { label: "Overdue", color: "red" },
};

const COLOR_TO_PILL = {
  green: { bg: "#dcfce7", text: "#166534", dot: "#22c55e" },
  red:   { bg: "#fee2e2", text: "#991b1b", dot: "#ef4444" },
  amber: { bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
  blue:  { bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
  gray:  { bg: "#f1f5f9", text: "#475569", dot: "#94a3b8" },
};

export function resolveStatus(raw, overrides = []) {
  if (raw === null || raw === undefined || raw === "") return null;
  const key = String(raw).trim().toUpperCase();
  // Admin override has priority
  const ov = (overrides || []).find((m) => String(m.raw || "").trim().toUpperCase() === key);
  if (ov) return { label: ov.label || TITLECASE(raw), color: ov.color || "gray" };
  // Built-in defaults
  if (DEFAULT_STATUS_MAP[key]) return DEFAULT_STATUS_MAP[key];
  // Fallback: pretty-print and pick a sensible neutral colour
  return { label: TITLECASE(raw), color: "gray" };
}

export function StatusPill({ value, type = "status", mappings }) {
  if (!value) return <span className="wl-pill wl-pill-neutral" data-testid="pill-empty">—</span>;
  const resolved = resolveStatus(value, mappings);
  if (!resolved) return null;
  const c = COLOR_TO_PILL[resolved.color] || COLOR_TO_PILL.gray;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
      style={{ background: c.bg, color: c.text }}
      data-testid={`pill-${type}-${String(value).toLowerCase()}`}
      title={String(value)}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.dot }} />
      {resolved.label}
    </span>
  );
}

export function Avatar({ name, size = 32 }) {
  const [bg, fg] = colorFromString(name || "?");
  return (
    <div
      className="inline-flex items-center justify-center font-semibold rounded-full"
      style={{ background: bg, color: fg, width: size, height: size, fontSize: size * 0.38 }}
      data-testid="avatar"
    >
      {initials(name)}
    </div>
  );
}

function BrandInitials({ name, size }) {
  const [bg, fg] = colorFromString(name || "?");
  return (
    <div
      className="inline-flex items-center justify-center font-semibold shrink-0"
      style={{ background: bg, color: fg, width: size, height: size, borderRadius: 10, fontSize: size * 0.32 }}
      data-testid="brand-logo-initials"
    >
      {initials(name)}
    </div>
  );
}

export function BrandLogo({ name, logoUrl, size = 40 }) {
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => { setBroken(false); }, [logoUrl]);
  if (logoUrl && !broken) {
    return (
      <img
        src={logoUrl}
        alt={name}
        style={{ width: size, height: size, borderRadius: 10, objectFit: "contain", background: "#fff" }}
        className="border border-[var(--border-default)] shrink-0"
        data-testid="brand-logo"
        draggable={false}
        onError={() => setBroken(true)}
      />
    );
  }
  return <BrandInitials name={name} size={size} />;
}

export function SheetHealthDot({ health }) {
  const status = health?.status || "not_connected";
  if (status === "connected") {
    const ago = health?.checked_at ? timeAgo(health.checked_at) : null;
    return (
      <span className="inline-flex items-center gap-2 text-xs text-[var(--text-secondary)]" data-testid="sheet-health-connected">
        <span className="wl-dot wl-dot-green" />
        Connected{ago ? ` · ${ago}` : ""}
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-[#991b1b]" title={health?.error} data-testid="sheet-health-error">
        <span className="wl-dot wl-dot-red" />
        {health?.error?.slice(0, 36) || "Sheet error"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-xs text-[var(--text-secondary)]" data-testid="sheet-health-none">
      <span className="wl-dot wl-dot-grey" />
      Not connected
    </span>
  );
}

export function Skeleton({ className = "h-4 w-full", ...rest }) {
  return <div className={`wl-skeleton ${className}`} {...rest} />;
}

export function EmptyState({ title, subtitle, action }) {
  return (
    <div className="wl-card p-12 flex flex-col items-center justify-center gap-3 text-center" data-testid="empty-state">
      <div className="text-base font-medium text-[var(--text-primary)]">{title}</div>
      {subtitle && <div className="text-sm text-[var(--text-secondary)] max-w-md">{subtitle}</div>}
      {action}
    </div>
  );
}

export function ProgressRing({ value = 0, size = 120, stroke = 12, color = "#e8613a" }) {
  const pct = Math.max(0, Math.min(100, value));
  // Keep 1 decimal of precision below 10% so e.g. 1.5% doesn't visually round to 2%.
  const label = pct < 10 && pct !== Math.floor(pct) ? `${Math.round(pct * 10) / 10}%` : `${Math.round(pct)}%`;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }} data-testid="progress-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#f1f1ee" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke}
          fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center font-display font-semibold tracking-tight"
        style={{ color, fontSize: size * 0.26 }}
        data-testid="progress-ring-label"
      >
        {label}
      </div>
    </div>
  );
}

export function StackedBar({ segments = [], height = 18 }) {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0) || 1;
  return (
    <div className="w-full" data-testid="stacked-bar">
      <div className="flex w-full overflow-hidden rounded-full bg-[#f1f1ee]" style={{ height }}>
        {segments.map((s) => (
          <div key={`seg-${s.label}`} title={`${s.label}: ${s.value}`}
            style={{ width: `${(s.value / total) * 100}%`, background: s.color }} />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)]">
        {segments.map((s) => (
          <span key={`legend-${s.label}`} className="inline-flex items-center gap-1.5">
            <span style={{ width: 8, height: 8, background: s.color, borderRadius: 9999 }} />
            {s.label} <span className="text-[var(--text-primary)] font-semibold">{s.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function Pagination({ page, pageCount, onChange }) {
  if (pageCount <= 1) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <button onClick={() => onChange(Math.max(1, page - 1))} disabled={page <= 1}
        className="wl-btn-ghost" data-testid="page-prev">Prev</button>
      <span className="text-[var(--text-secondary)]">Page {page} of {pageCount}</span>
      <button onClick={() => onChange(Math.min(pageCount, page + 1))} disabled={page >= pageCount}
        className="wl-btn-ghost" data-testid="page-next">Next</button>
    </div>
  );
}
